import {
  GoABlock,
  GoAButton,
  GoAButtonGroup,
  GoACallout,
  GoACheckbox,
  GoAFormItem,
  GoAModal,
  GoASpacer,
} from '@abgov/react-components';
import { useEffect, useRef, useState } from 'react';
import { DamageCardComponent } from './damage-card';
import {
  DamageSketch,
  DesktopDamageSelectorLayoutConfiguration,
  MobileDamageSelectorLayoutConfiguration,
} from '../sketches/damage';
import p5 from 'p5';

import { InitialPointOfImpact } from '../../models/initial-point-of-impact';
import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import { updateCollision } from '../../state/slices/collisionSlice';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { WizardButtonGroup } from '../wizard/wizard-button-group-component';
import { Collision, DamageSelection } from '../../models/collision';
import { useIsDesktop } from '../../utils/isDesktop';
import {
  isAttachment,
  setReviewDamageModified,
} from '../../state/slices/configurationSlice';
import { setPriority } from 'os';
import {
  pushNotification,
  removeAllNotifications,
} from '../../state/slices/notificationSlice';
import { NotificationType } from '../notifications/notification-component';
import {
  getReturnToValue,
  IsInReviewEditMode,
} from '../wizard/wizard-component';
import { flow, get } from 'lodash';

export interface DamageComponentProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const DamageComponent = ({
  navigationConfiguration,
}: DamageComponentProps) => {
  const damageEnteredErrorText = 'Select at least one damage area';
  const headerErrorText = 'Select at least one damage area';
  const dispatch = useAppDispatch();
  const isDesktop = useIsDesktop();

  const [nextPage, setNextPage] = useState('');

  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );
  const p5Ref = useRef<HTMLDivElement>(null);
  const containerDiv = useRef<HTMLDivElement>(null);

  const [
    initialPointOfImpactLocationList,
    setInitialPointOfImpactLocationList,
  ] = useState<InitialPointOfImpact[]>([]);

  const [damageSketch, setDamageSketch] = useState<DamageSketch>(
    new DamageSketch(),
  );

  const [damageSelectionsList, setDamageSelectionsList] = useState<
    DamageSelection[]
  >(stateCollision?.collision?.vehicles?.[0]?.damage?.damageSelection || []);

  const [poiCards, setPoiCards] = useState<JSX.Element[]>([]);

  const [damageEnteredError, setDamageEnteredError] = useState('');

  useEffect(() => {
    const pois: InitialPointOfImpact[] = [];

    stateConfiguration.configuration.pointOfImpact.forEach((poi: any) => {
      pois.push(new InitialPointOfImpact(poi.id, poi.value));
    });

    setInitialPointOfImpactLocationList(pois);
  }, [stateConfiguration.configuration.pointOfImpact]);

  useEffect(() => {
    updateNextPage();
  }, [damageSelectionsList]);

  const validateSelections = () => {
    if (damageSelectionsList.length == 0) {
      setDamageEnteredError(damageEnteredErrorText);
      dispatch(
        pushNotification({
          body: headerErrorText,
          notificationType: NotificationType.importantValidationError,
        }),
      );
      return false;
    }
    return true;
  };

  const save = () => {
    let updatedCollision: Collision = { ...stateCollision.collision };

    if (
      navigationConfiguration.updateMetadata &&
      navigationConfiguration.stepId
    ) {
      const stepUpdated = navigationConfiguration.updateMetadata(
        updatedCollision,
        navigationConfiguration.stepId!,
        navigationConfiguration.stageCompleted
          ? navigationConfiguration.stageCompleted
          : false,
        !navigationConfiguration.stageCompleted,
      );

      if (stepUpdated && updatedCollision.metadata) {
        updatedCollision.metadata = {
          ...updatedCollision.metadata,
          formState: stepUpdated,
        };
      }
    }

    const hasAttachment = damageSelectionsList.find((item) =>
      isAttachment(parseInt(item.pointOfImpactId), stateConfiguration),
    );

    const baseVehicle = updatedCollision.vehicles?.[0] || {};
    const baseDamage = updatedCollision.vehicles?.[0]?.damage || {};

    if (!hasAttachment) {
      updatedCollision.vehicles = [
        {
          ...baseVehicle,
          damage: {
            ...baseDamage,
            attachment: undefined,
            attachmentDetails: undefined,
            loadDetail: undefined,
            loadSpillage: undefined,
            damageSelection: [...damageSelectionsList],
          },
        },
      ];
    } else {
      updatedCollision.vehicles = [
        {
          ...baseVehicle,
          damage: {
            ...baseDamage,
            damageSelection: [...damageSelectionsList],
          },
        },
      ];
    }
    dispatch(updateCollision(updatedCollision));

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  useEffect(() => {
    if (p5Ref.current) {
      let startCount = 1;

      const p5Instance = new p5(
        damageSketch.sketch(
          containerDiv.current!,
          damageSelectionsList,
          initialPointOfImpactLocationList,
          startCount,
          handleDamageListUpdate,
          true,
          isDesktop
            ? new DesktopDamageSelectorLayoutConfiguration(
                initialPointOfImpactLocationList,
              )
            : new MobileDamageSelectorLayoutConfiguration(
                initialPointOfImpactLocationList,
              ),
        ),
        p5Ref.current,
      );

      return () => {
        p5Instance.remove();
      };
    }
  }, [initialPointOfImpactLocationList]);

  useEffect(() => {
    if (damageSelectionsList && damageSelectionsList.length > 0) {
      setDamageEnteredError('');
      dispatch(removeAllNotifications());

      let cardCount = 0;
      const cardList: JSX.Element[] = [];
      const arr = [...damageSelectionsList];
      arr!
        .sort((a, b) => a.clickTime! - b.clickTime!)
        .map((damageSelector) => {
          cardCount++;

          cardList.push(
            <div key={damageSelector.pointOfImpactId}>
              <DamageCardComponent
                id={parseInt(damageSelector.pointOfImpactId!)}
                order={cardCount}
                isInitalPointOfImpact={false}
                initialPointsOfImpact={initialPointOfImpactLocationList}
                navigateHandler={() => {
                  window.location.href = `#damage`;
                }}
                removeHandler={(lookupId) => {
                  damageSketch.removeDamageSelection(
                    lookupId.toString(),
                    handleDamageListRemove,
                  );
                }}
              />
            </div>,
          );
        });

      setPoiCards(cardList);
    } else {
      setPoiCards([]);
    }

    if (IsInReviewEditMode()) {
      dispatch(removeAllNotifications());
      dispatch(
        pushNotification({
          body: "You can review or make changes to your inputs in this section. If you need to make changes, make sure to click 'Save and continue' to apply them and continue to provide your input in all of the steps in this section.",
          notificationType: NotificationType.importantWarning,
        }),
      );
    }
  }, [initialPointOfImpactLocationList, damageSelectionsList]);

  const isAttachmentFlowRequired = () => {
    let flowRequired = false;

    if (
      damageSelectionsList.some((item) =>
        isAttachment(parseInt(item.pointOfImpactId), stateConfiguration),
      ) &&
      (!stateCollision.collision.vehicles ||
        (stateCollision.collision.vehicles &&
          stateCollision.collision.vehicles.length > 0 &&
          stateCollision.collision.vehicles[0].damage &&
          (!stateCollision.collision.vehicles[0].damage.damageSelection ||
            !stateCollision.collision.vehicles[0].damage.attachmentDetails ||
            !stateCollision.collision.vehicles[0].damage.loadDetail ||
            !stateCollision.collision.vehicles[0].damage.loadSpillage)))
    ) {
      flowRequired = true;
    }

    return flowRequired;
  };

  const updateNextPage = () => {
    let pageConfig = 'error';

    if (isAttachmentFlowRequired()) {
      pageConfig = navigationConfiguration.pagingConfiguration.find(
        (item) => item.attachment !== undefined,
      )!.attachment;
    } else {
      pageConfig = navigationConfiguration.pagingConfiguration.find(
        (item) => item.sticker !== undefined,
      )!.sticker;
    }
    setNextPage(pageConfig);
  };

  const handleDamageListRemove = (itemToRemove: {
    pointOfImpactId: string;
    value: boolean;
    isInitialPointOfImpact: boolean;
    clickTime: number;
  }) => {
    if (IsInReviewEditMode()) {
      dispatch(setReviewDamageModified(true));
    }
    setDamageSelectionsList(
      damageSelectionsList.filter(
        (x) =>
          x.pointOfImpactId?.toString() !==
          itemToRemove.pointOfImpactId.toString(),
      ),
    );
  };

  const handleDamageListUpdate = (newDamageSelection: {
    pointOfImpactId: string;
    value: boolean;
    isInitialPointOfImpact: boolean;
    clickTime: number;
  }) => {
    setDamageSelectionsList((prevList) => {
      const itemExists = prevList.some(
        (item) =>
          item.pointOfImpactId?.toString() ===
          newDamageSelection.pointOfImpactId.toString(),
      );

      // If the item exists, use map to return the updated array
      if (itemExists) {
        return prevList.map((item) =>
          item.pointOfImpactId?.toString() ===
          newDamageSelection.pointOfImpactId?.toString()
            ? newDamageSelection
            : item,
        );
      }

      if (IsInReviewEditMode()) {
        dispatch(setReviewDamageModified(true));
      }
      // If the item does not exist, add the new item
      return [...prevList, newDamageSelection];
    });
  };

  return (
    <div>
      <div>
        <h1>Vehicle damage</h1>
        Please provide the required information about the damage to your
        vehicle.
        <h2>Indicate your vehicle damage areas</h2>
        Click on the interactive indicators below to select the locations of
        your vehicle damage.
      </div>
      <GoASpacer vSpacing={'xl'} />
      <GoACallout
        heading="Windshield and back window damage"
        type="information"
        size="medium"
      >
        You can enter damages to the windshield or back window in the additional
        damages text field on the last step of this section
      </GoACallout>
      <GoAFormItem error={damageEnteredError}>
        <div ref={containerDiv} className="damage-vehicle">
          <div ref={p5Ref} />
        </div>
      </GoAFormItem>
      <div>{poiCards}</div>
      <div>
        <WizardButtonGroup
          nextPage={nextPage}
          validate={validateSelections}
          save={save}
          showViewSummaryButton={!IsInReviewEditMode()}
          goToReview={navigationConfiguration.goToReview}
          navigate={navigationConfiguration.navigate}
        />
      </div>
    </div>
  );
};
